import type { Pathname } from '@shared/navigation/routes.gen';

export const SILENT_UPDATE_COOLDOWN_KEY = 'curium-silent-update';
export const SILENT_UPDATE_ATTEMPTED_BUILD_KEY = 'curium-silent-update-build';
export const SILENT_UPDATE_OVERLAY_ID = 'curium-silent-update-overlay';

export const SILENT_UPDATE_COOLDOWN_MS = 30_000;
export const FALLBACK_NOTIFICATION_TIMEOUT_MS = 30 * 60 * 1000;

/**
 * Session marker written immediately before an updater-triggered reload ("Reload now"
 * or a silent update). While the grace window is active after the reload, server-reported
 * build mismatches are ignored: right after a deploy the backend's per-process 30 s version
 * cache can still report the OLD build to a client that just loaded the NEW bundle, which
 * used to re-show "New version available" seconds after the user updated.
 */
export const UPDATE_RELOAD_GRACE_KEY = 'curium-update-reload-at';
export const UPDATE_RELOAD_GRACE_MS = 60_000;

/**
 * How long a server-reported build mismatch must persist before the client trusts it.
 * The first sighting arms a delayed confirmation refetch; only a second sighting of the
 * SAME build after this window may surface the update UI. Sized to outlast the backend's
 * 30 s per-process version cache (getAppBuildVersion), so a stale process observed once
 * right after a deploy — or a one-off flap — never shows a phantom prompt.
 */
export const VERSION_MISMATCH_CONFIRM_DELAY_MS = 35_000;

/**
 * How long the "New version available" toast stays snoozed after the user closes it
 * via the X ("remind me later"). Once this window elapses the toast re-appears if the
 * version is still mismatched. The "Later" button, by contrast, dismisses the build
 * outright — no re-prompt until a newer build ships.
 */
export const VERSION_SNOOZE_MS = 60 * 60 * 1000;

export const VERSION_CHECK_EXCLUDED_PATH_PREFIXES = [
    '/maintenance',
    '/login',
    '/callback',
    '/admin-consent',
    '/document-sources/consent',
    '/logoutin',
    '/public',
] as const satisfies readonly Pathname[];

export function isVersionCheckExcludedPath(pathname: string): boolean {
    return VERSION_CHECK_EXCLUDED_PATH_PREFIXES.some(
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );
}
